/**
 * Public, unauthenticated charge page reached by the payer from a WhatsApp /
 * email link (`/pay/:token`). Consumes the curated
 * `GET /public/v1/charges/{token}` endpoint and renders the PIX QR Code,
 * copy-and-paste code, and current status. All payment UX lives here — the
 * WhatsApp template only carries a CTA button pointing to this page.
 *
 * The page is generic: it works for any Charge origin (CT mensalidade,
 * reserva avulsa, day use, etc.). The contextual title comes from
 * `payload.description`, composed by the calling domain when the Charge was
 * created.
 *
 * Compliance: Asaas seal + processed-by clause rendered in the footer
 * regardless of status, as required by the BaaS rulebook on every
 * payer-facing surface.
 */

import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { analyticsService } from '@meiuca/analytics'
import { AsaasSeal, Body, Caption, H1, H2 } from '@meiuca/ui-components'
import { Check } from 'lucide-react'
import { NotFoundError } from '@meiuca/service'
import { formatCurrency, formatDateForUI, formatDateTimeForUI } from '@meiuca/web-foundation'
import { SCREEN_NAMES } from '@/constants/analyticsEvents'
import { DownloadAppBanner } from '@/components/DownloadAppBanner'
import { PixPayment } from '@/components/PixPayment'
import { OVERDUE_PIX_NOTICE, isOverdue } from '@/lib/overdue'
import {
  publicChargeService,
  type PublicChargePayload
} from '@/services/publicChargeService'

type LoadState =
  | { kind: 'loading' }
  | { kind: 'not-found' }
  | { kind: 'error'; message: string }
  | { kind: 'ok'; payload: PublicChargePayload }

export default function PayChargePage() {
  const { token } = useParams<{ token: string }>()
  const [state, setState] = useState<LoadState>({ kind: 'loading' })

  useEffect(() => {
    document.title = 'Pagamento · Meiuca'
    analyticsService.trackScreenView(SCREEN_NAMES.PAY_CHARGE)
  }, [])

  useEffect(() => {
    if (!token) {
      setState({ kind: 'not-found' })
      return
    }
    setState({ kind: 'loading' })
    publicChargeService
      .getByToken(token)
      .then((payload) => setState({ kind: 'ok', payload }))
      .catch((err) => {
        if (err instanceof NotFoundError) {
          setState({ kind: 'not-found' })
        } else {
          setState({ kind: 'error', message: 'Não conseguimos carregar esta cobrança agora.' })
        }
      })
  }, [token])

  return (
    <main className="min-h-screen bg-background-primary flex flex-col">
      <PageHeader />

      <section className="flex-1 flex items-start justify-center px-4 py-10 md:py-16">
        <div className="w-full max-w-md space-y-4">
          {state.kind === 'loading' && <LoadingState />}
          {state.kind === 'not-found' && <NotFoundState />}
          {state.kind === 'error' && <ErrorState message={state.message} />}
          {state.kind === 'ok' && <ChargeView payload={state.payload} />}
          {state.kind === 'ok' && <DownloadAppBanner />}
        </div>
      </section>

      <PageFooter />
    </main>
  )
}

function PageHeader() {
  return (
    <header className="border-b border-gray-200/40 bg-background-white/80 backdrop-blur-md">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center h-16 md:h-20">
          <div className="flex items-center gap-2">
            <img src="/favicon.svg" alt="Meiuca" className="w-7 h-7" width={28} height={28} />
            <span className="text-lg md:text-xl font-bold text-text-primary">Meiuca</span>
          </div>
        </div>
      </div>
    </header>
  )
}

function PageFooter() {
  return (
    <footer className="border-t border-gray-200/40 bg-background-secondary py-8 px-5">
      <div className="max-w-md mx-auto flex flex-col items-center gap-3 text-center">
        <AsaasSeal size="sm" />
        <Caption className="text-text-tertiary">
          Pagamento processado por Asaas IP
        </Caption>
      </div>
    </footer>
  )
}

function LoadingState() {
  return (
    <div className="rounded-2xl bg-background-white p-8 text-center shadow-sm">
      <Body className="text-text-secondary">Carregando cobrança…</Body>
    </div>
  )
}

function NotFoundState() {
  return (
    <div className="rounded-2xl bg-background-white p-8 text-center shadow-sm">
      <H2 className="mb-2">Cobrança não encontrada</H2>
      <Body className="text-text-secondary">
        O link pode estar incorreto ou ter expirado. Verifique a mensagem original ou peça uma nova
        cobrança para quem te enviou.
      </Body>
    </div>
  )
}

function ErrorState({ message }: { message: string }) {
  return (
    <div className="rounded-2xl bg-background-white p-8 text-center shadow-sm">
      <H2 className="mb-2">Algo deu errado</H2>
      <Body className="text-text-secondary">{message} Tente novamente em alguns instantes.</Body>
    </div>
  )
}

function ChargeView({ payload }: { payload: PublicChargePayload }) {
  const isOpen = payload.status === 'open' || payload.status === 'partiallyPaid'
  const overdue = isOpen && isOverdue(payload.dueAt)

  return (
    <div className="rounded-2xl bg-background-white p-6 md:p-8 shadow-sm space-y-6">
      <header className="space-y-2">
        <StatusBadge status={payload.status} overdue={overdue} />
        <H1 className="text-2xl md:text-3xl">{payload.description ?? 'Cobrança'}</H1>
      </header>

      <ChargeSummary
        amountCents={payload.amountCents}
        dueAt={payload.dueAt}
        paidAt={payload.paidAt}
        status={payload.status}
      />

      {payload.status === 'paid' && <PaidConfirmation paidAt={payload.paidAt} />}

      {payload.status === 'canceled' && <CanceledNotice />}

      {isOpen && payload.pix && (
        <div className="space-y-3">
          {overdue && payload.dueAt && (
            <Body className="text-feedback-error text-center">{OVERDUE_PIX_NOTICE(payload.dueAt)}</Body>
          )}
          <PixPayment payload={payload.pix.payload} qrCodeBase64={payload.pix.qrCodeBase64} />
        </div>
      )}

      {isOpen && !payload.pix && (
        <Body className="text-text-secondary">
          {overdue
            ? 'Este Pix venceu. Fale com quem te enviou a cobrança para receber um novo.'
            : 'A cobrança ainda está sendo preparada. Atualize a página em alguns instantes.'}
        </Body>
      )}
    </div>
  )
}

function StatusBadge({ status, overdue }: { status: PublicChargePayload['status']; overdue: boolean }) {
  const variants = {
    open: { label: 'Aguardando pagamento', className: 'bg-yellow-100 text-yellow-800' },
    partiallyPaid: { label: 'Pagamento parcial', className: 'bg-yellow-100 text-yellow-800' },
    paid: { label: 'Pago', className: 'bg-green-100 text-green-800' },
    canceled: { label: 'Cancelada', className: 'bg-gray-100 text-gray-700' }
  } as const
  const v = overdue ? { label: 'Vencida', className: 'bg-red-100 text-red-800' } : variants[status]
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${v.className}`}>
      {v.label}
    </span>
  )
}

function ChargeSummary({
  amountCents,
  dueAt,
  paidAt,
  status
}: {
  amountCents: number
  dueAt: string | null
  paidAt: string | null
  status: PublicChargePayload['status']
}) {
  return (
    <dl className="grid grid-cols-2 gap-y-3 gap-x-4 border-y border-gray-100 py-4">
      <dt className="text-text-tertiary text-sm">Valor</dt>
      <dd className="text-right font-semibold text-text-primary text-base">
        {formatCurrency(amountCents)}
      </dd>

      {status === 'paid' && paidAt ? (
        <>
          <dt className="text-text-tertiary text-sm">Pago em</dt>
          <dd className="text-right text-text-primary">{formatDateTimeForUI(paidAt)}</dd>
        </>
      ) : (
        <>
          <dt className="text-text-tertiary text-sm">Vencimento</dt>
          <dd className="text-right text-text-primary">{dueAt ? formatDateForUI(dueAt) : '—'}</dd>
        </>
      )}
    </dl>
  )
}

function PaidConfirmation({ paidAt }: { paidAt: string | null }) {
  return (
    <div className="flex items-start gap-3 rounded-xl bg-green-50 p-4 text-green-900">
      <Check className="mt-0.5 flex-shrink-0" size={20} />
      <div>
        <Body className="font-semibold">Pagamento recebido</Body>
        {paidAt && <Caption className="text-green-800">{formatDateTimeForUI(paidAt)}</Caption>}
      </div>
    </div>
  )
}

function CanceledNotice() {
  return (
    <div className="rounded-xl bg-gray-50 p-4 text-text-secondary">
      <Body>Esta cobrança foi cancelada e não está mais disponível para pagamento.</Body>
    </div>
  )
}

